import React, { useEffect, useState } from "react";
import styles from "./SearchContacts.module.css";
import {
  DefaultButton,
  DetailsList,
  Dropdown,
  IColumn,
  IDropdownOption,
  IDropdownStyles,
  IStackStyles, Label,
  PrimaryButton,
  SelectionMode,
  Spinner,
  Stack,
  StackItem,
  TextField,
  TooltipOverflowMode,
  MessageBarType,
} from "@fluentui/react";
import { displayMessageBar } from "../LayoutWithAuth/LayoutWithAuth";
import strings from "../../loc/strings";
import {
  defaultContactValues,
  initialSearchContacts,
  _columns,
} from "./SearchContactsUtils";
import {
  _fetchCountryOptions,
  _authorityOptions,
  searchContactsCriteriaForm,
} from "../../services/SearchServices/SearchContactsService";
import Pagination from "../Pagination/Pagination";
import { useAuth } from "react-oidc-context";
import QuickSearch from "../Admin/QuickSearch/QuickSearch";
import { Authority, ContactPerson, Country } from "../../model/SearchContact";
import { TooltipHost } from "@fluentui/react/lib/Tooltip";

interface SearchContactsProps {}

export interface SearchContactsCriteria
  extends Pick<
    ContactPerson,
    "firstName" | "lastName" | "country" | "authority"
  > {}

export interface SearchContactsResults {
  results: ContactPerson[];
}
const stylesButtons: Partial<IStackStyles> = {
  root: {
    marginTop: "5px",

    marginRight: "10px",
  },
};
const stackStylesNoResults: Partial<IStackStyles> = {
  root: {
    paddingTop: "16px",
  },
};
const customDropdownStyles: Partial<IDropdownStyles> = {
  dropdown: { width: 250 },
};

const customTextFieldStyles = {
  fieldGroup: {
    width: 250,
  },
};
function SearchContacts(props: SearchContactsProps) {
  const [searchContacts, setSearchContacts] = useState(initialSearchContacts);

  const _handleSearch = () => {
    const searchContactsCriteria: SearchContactsCriteria = {
      firstName: searchContacts.firstName,
      lastName: searchContacts.lastName,
      country: searchContacts.country,
      authority: searchContacts.authority,
    };
    return searchContactsCriteria;
  };

  const [columnState, setColumnState] = React.useState<IColumn[]>(
    _buildColumns(_columns)
  );
  const [countryOptions, setCountryOptions] = useState<
    IDropdownOption<Country>[]
  >([]);
  const [authorityOptions, setAuthorityOptions] = useState<
    IDropdownOption<Authority>[]
  >([]);

  const [selectedKeyCountry, setSelectedKeyCountry] = useState(
    strings.searchContactsSelectKey
  );
  const [selectedNameCountry, setSelectedNameCountry] = useState("");
  const [selectedKeyAuthority, setSelectedKeyAuthority] = useState(
    strings.searchContactsSelectKey
  );

  const [isResultsFetched, setIsResultsFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [filteredResults, setFilteredResults] = useState<SearchContactsResults>(
    {
      results: [defaultContactValues],
    }
  );

  const [searchResults, setSearchResults] = useState<SearchContactsResults>({
    results: [defaultContactValues],
  });
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredResultsSliced, setFilteredResultsSliced] = useState<
    SearchContactsResults["results"]
  >([]);

  const _handleDropDownChange = (
    item: IDropdownOption | undefined,
    dropdownName: string
  ) => {
    if (item) {
      switch (dropdownName) {
        case strings.searchContactsSwitchCaseCountry:
          setSelectedKeyCountry(item.key as string);
          setSelectedNameCountry(item.text);
          setSearchContacts({
            ...searchContacts,
            country: item.text,
          });
          break;
        case strings.searchContactsSwitchCaseAuthority:
          setSelectedKeyAuthority(item.key as string);
          setSearchContacts({
            ...searchContacts,
            authority: item.key as string,
          });
          break;

        default:
          break;
      }
    }
  };
  const handleItemsProcessed = (processedData: any) => {
    setTotalItems(processedData.length);

    if (processedData.length > 0) {
      const startIndex = 0 * itemsPerPage;
      const endIndex = Math.min(
        startIndex + itemsPerPage,
        processedData.length
      );
      const itemsOnCurrentPage = processedData.slice(startIndex, endIndex);
      setFilteredResultsSliced(itemsOnCurrentPage);
      setFilteredResults({ results: processedData });
      setCurrentPage(1);
    } else {
      setFilteredResults({ results: [defaultContactValues] });
      setFilteredResultsSliced([defaultContactValues]);
      setCurrentPage(1);
    }
  };
  const onChangeItemsPerPage = (
    event: React.FormEvent<HTMLDivElement>,
    item: IDropdownOption
  ): void => {
    const _itemsPerPage = item.key as number;

    setCurrentPage(1);

    const newSliceStart = 0;
    const newSliceEnd = Math.min(_itemsPerPage, totalItems);

    setFilteredResultsSliced(
      filteredResults.results.slice(newSliceStart, newSliceEnd)
    );
    setItemsPerPage(_itemsPerPage);
  };

  function onPageChange(selectedPage: number) {
    setCurrentPage(selectedPage);
    setFilteredResultsSliced(
      filteredResults.results.slice(
        (selectedPage - 1) * itemsPerPage,
        (selectedPage - 1) * itemsPerPage + itemsPerPage
      )
    );
  }

  const auth = useAuth();
  useEffect(() => {
    const fetchCountryOptions = async () => {
      try {
        const options = await _fetchCountryOptions(
          selectedKeyCountry,
          auth.user?.id_token ?? ""
        );

        setCountryOptions(options);
      } catch (error) {
        console.error(strings.searchContactsErrorFetchingCountries, error);
      }
    };

    fetchCountryOptions();
  }, [selectedKeyCountry]);

  useEffect(() => {
    const fetchAuthorityOptions = async () => {
      try {
        const countryFilter =
          selectedKeyCountry !== strings.searchContactsSelectKey
            ? `?country=${selectedNameCountry}`
            : "";

        const options = await _authorityOptions(
          selectedKeyAuthority,
          auth.user?.id_token ?? "",
          countryFilter
        );

        setAuthorityOptions(options);
      } catch (error) {
        console.error(strings.searchContactsErrorFetchingAuthorities, error);
      }
    };

    fetchAuthorityOptions();
  }, [selectedKeyAuthority, selectedKeyCountry]);

  function _buildColumns(cols: IColumn[]): IColumn[] {
    cols.forEach((column: IColumn) => {
      column.onColumnClick = _onColumnClick;
    });

    return cols;
  }

  const handleSearch = async () => {
    try {
      setLoading(true);
      setErrorMessage(null);
      // Clear any existing error message
      displayMessageBar(MessageBarType.info, "", false);

      const data = await searchContactsCriteriaForm(
        _handleSearch(),
        auth.user?.id_token
      );

      // Check if the response is an error
      if ('errors' in data) {
        // Handle validation errors
        const errorMessages = data.errors?.join(', ') || 'An error occurred during search';
        setErrorMessage(errorMessages);
        setIsResultsFetched(false);
        // Display error message in parent component
        displayMessageBar(MessageBarType.error, errorMessages, false);
      } else {
        // If it's not an error, it must be SearchContactsResults
        const resultsData = data as SearchContactsResults;
        setSearchResults(resultsData);
        setTotalItems(resultsData.results.length);
        setFilteredResults(resultsData);
        setFilteredResultsSliced(
          resultsData.results.slice(
            (currentPage - 1) * itemsPerPage,
            (currentPage - 1) * itemsPerPage + itemsPerPage
          )
        );
        setIsResultsFetched(true);
      }
    } catch (error) {
      const errorMessage = 'An unexpected error occurred during search';
      setErrorMessage(errorMessage);
      setIsResultsFetched(false);
      // Display error message in parent component
      displayMessageBar(MessageBarType.error, errorMessage, false);
    } finally {
      setLoading(false);
    }
  };

  const handleButtonClick = () => {
    handleSearch();
    setCurrentPage(1);
    setIsResultsFetched(false);
    setItemsPerPage(10)
  };

  const onReset = () => {
    setIsResultsFetched(false);
    setSearchResults({ results: [] });
    setCurrentPage(1);
    setSearchContacts(initialSearchContacts);
    setSelectedKeyCountry(strings.searchContactsSelectKey);
    setSelectedKeyAuthority(strings.searchContactsSelectKey);
    setItemsPerPage(10);
    setErrorMessage(null);
    // Clear any displayed error message
    displayMessageBar(MessageBarType.info, "", false);
  };
  function caseInsensitiveSort(
    a: any,
    b: any,
    isSortedDescending: boolean
  ): number {
    const valA = a.toString().toLowerCase();
    const valB = b.toString().toLowerCase();

    if (valA < valB) {
      return isSortedDescending ? 1 : -1;
    } else if (valA > valB) {
      return isSortedDescending ? -1 : 1;
    } else {
      return 0;
    }
  }
  const columnOptions = _columns.map((column) => ({
    key: column.key,
    text: column.name,
  }));
  function sortItems(
    data: SearchContactsProps[],
    sortBy: string,
    isSortedDescending: boolean
  ): any[] {
    return data.slice().sort((a, b) => {
      return caseInsensitiveSort(
        a[sortBy as keyof typeof a],
        b[sortBy as keyof typeof b],
        isSortedDescending
      );
    });
  }

  function _onColumnClick(
    ev: React.MouseEvent<HTMLElement, MouseEvent> | undefined,
    column?: IColumn | undefined
  ): void {
    if (ev && column) {
      const { key, isSortedDescending } = column;

      const newIsSortedDescending = column.isSorted
        ? !isSortedDescending
        : false;

      setColumnState((prevColumns) =>
        prevColumns.map((col) => ({
          ...col,
          isSorted: col.key === key,
          isSortedDescending: col.key === key ? newIsSortedDescending : false,
        }))
      );

      const sortedData = sortItems(
        filteredResults.results,
        key,
        newIsSortedDescending
      );

      setFilteredResults({ results: sortedData });
      setFilteredResultsSliced(sortedData.slice(0, itemsPerPage));
      setCurrentPage(1);
    }
  }

  function _renderItemColumn(item?: any, index?: number, column?: IColumn) {
    if (item && column) {
      const fieldContent = item[column.fieldName as keyof any] as string;
      return (
        <TooltipHost
          overflowMode={TooltipOverflowMode.Parent}
          content={fieldContent}
        >
          {fieldContent}
        </TooltipHost>
      );
    }
  }

  const addTooltipToColumnHeader = (cols: IColumn[]) => {
    cols.forEach((col) => {
      col.onRenderHeader = (props) => {
        return (
          <TooltipHost
            overflowMode={TooltipOverflowMode.Parent}
            content={props?.column.name}
          >
            {props?.column.name}
          </TooltipHost>
        );
      };
    });
    return cols;
  };

  const renderedAuthorityOptions: React.ReactElement[] = authorityOptions.map(
    (option) => (
      <TooltipHost key={option.key} content={option.title}></TooltipHost>
    )
  );
  const renderedCountryOptions: React.ReactElement[] = countryOptions.map(
    (option) => (
      <TooltipHost key={option.key} content={option.title}></TooltipHost>
    )
  );
  return (
    <Stack>
      <StackItem className={styles.SearchContactsTopLabel}>
        {strings.searchContacts}
      </StackItem>

      <StackItem styles={{ root: { marginBottom: "2px" } }}>
        <Stack horizontal className={styles.SearchContactsFormStack}>
          <StackItem>
            <Stack horizontal className={styles.SearchContactsFormFieldsStack}>
              <StackItem className={styles.SearchContactsFirstPairFormField}>
                <TextField
                  value={searchContacts.firstName || ""}
                  label={strings.searchContactsFirstName}
                  placeholder={strings.searchContactsTypeFirstNamePlaceholder}
                  name={strings.searchContactsTextFieldFirstName}
                  onChange={(event, newValue) => {
                    setSearchContacts({
                      ...searchContacts,
                      firstName: newValue ?? "",
                    });
                  }}
                  onKeyDown={(event) => {
                    if (event.key === strings.searchContactsEventKeyEnter) {
                      handleButtonClick();
                    }
                  }}
                />
              </StackItem>
              <StackItem className={styles.SearchContactsFirstPairFormField}>
                <TextField
                  label={strings.searchContactsLastName}
                  value={searchContacts.lastName}
                  placeholder={strings.searchContactsTypeLastNamePlaceholder}
                  name={strings.searchContactsTextFieldLastName}
                  onChange={(event, newValue) => {
                    setSearchContacts({
                      ...searchContacts,
                      lastName: newValue ?? "",
                    });
                  }}
                  onKeyDown={(event) => {
                    if (event.key === strings.searchContactsEventKeyEnter) {
                      handleButtonClick();
                    }
                  }}
                />
              </StackItem>
              <Stack
                horizontal
                className={styles.SearchContactsFormFieldsStack}
              >
                <StackItem className={styles.SearchContactsSecondPairFormField}>
                  <Dropdown
                    label={strings.searchContactsCountry}
                    selectedKey={selectedKeyCountry}
                    placeholder={strings.searchContactsSelectCountry}
                    options={countryOptions}
                    onChange={(event, item) =>
                      _handleDropDownChange(
                        item,
                        strings.searchContactsSwitchCaseCountry
                      )
                    }
                    {...renderedAuthorityOptions}
                    data-testid="country-dropdown"
                  ></Dropdown>
                </StackItem>
                <StackItem className={styles.SearchContactsSecondPairFormField}>
                  <Dropdown
                    label={strings.searchContactsAuthority}
                    selectedKey={selectedKeyAuthority}
                    placeholder={strings.searchContactsSelectAuthority}
                    options={authorityOptions}
                    onChange={(event, item) =>
                      _handleDropDownChange(
                        item,
                        strings.searchContactsSwitchCaseAuthority
                      )
                    }
                    {...renderedCountryOptions}
                    data-testid="authority-dropdown"
                  ></Dropdown>
                </StackItem>
              </Stack>
            </Stack>
          </StackItem>
          <StackItem>
            <Stack horizontal>
              <StackItem>
                <Label styles={{ root: { color: 'transparent' } }}>&nbsp;</Label>
                <PrimaryButton
                  styles={stylesButtons}
                  onClick={() => {
                    handleButtonClick();
                  }}
                  text={strings.searchContactsSearchButton}
                />
              </StackItem>
              <StackItem>
                <Label styles={{ root: { color: 'transparent' } }}>&nbsp;</Label>
                <DefaultButton
                  styles={stylesButtons}
                  onClick={() => {
                    onReset();
                  }}
                  text={strings.searchContactsResetButton}
                />
              </StackItem>
            </Stack>
          </StackItem>
        </Stack>
      </StackItem>
      {isResultsFetched && searchResults.results.length > 0 ? (
        <Stack className={styles.QuickSearchStack}>
          <StackItem>
            <QuickSearch
              items={filteredResults.results}
              itemsOrg={searchResults.results}
              options={columnOptions}
              onItemsProcessed={handleItemsProcessed}
              dropdownStyles={customDropdownStyles}
              textFieldStyles={customTextFieldStyles}
            />
          </StackItem>
        </Stack>
      ) : (
        isResultsFetched &&
        !loading &&
        searchResults.results.length === 0 && (
          <StackItem styles={stackStylesNoResults}>
            <div>{strings.searchContactsNoContactsFound}</div>
          </StackItem>
        )
      )}
      <StackItem>
        {loading ? (
          <Spinner label={strings.spinnerLabel} />
        ) : isResultsFetched && searchResults.results.length > 0 ? (
          <DetailsList
            selectionMode={SelectionMode.none}
            layoutMode={1}
            columns={addTooltipToColumnHeader(columnState)}
            items={filteredResultsSliced}
            onColumnHeaderClick={_onColumnClick}
            onRenderItemColumn={_renderItemColumn}
          />
        ) : null}
      </StackItem>

      <StackItem>
        {isResultsFetched && !loading && (
          <Pagination
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onChangeItemsPerPage={onChangeItemsPerPage}
            totalItems={filteredResults.results.length > 0 ? totalItems : 0}
            onPageChange={onPageChange}
          />
        )}
      </StackItem>
    </Stack>
  );
}

export default SearchContacts;
